import React, { useEffect, useState } from "react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading.js";
import AddLead from "./addLead.js";
import ImportLead from "./importLead";
import { secretKey } from "./data";
import { attachCopyLogger } from "./logCopy";

const MySwal = withReactContent(Swal2);

/* ─── helpers ─── */
const formatDate = (raw) => {
  const d = new Date(raw);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const API = "https://apiftpsplit.ftpeople.pl";
const headers = {
  "Content-Type": "application/json",
  Authorization: secretKey,
};

/* ─── truncated note cell ─── */
const NoteCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="lq-empty">—</span>;
  const isLong = text.length > 80;
  return (
    <div
      className={`lq-note ${expanded ? "expanded" : ""}`}
      onClick={() => isLong && setExpanded(!expanded)}
      title={isLong && !expanded ? text : undefined}
    >
      {expanded || !isLong ? text : `${text.slice(0, 80)}…`}
      {isLong && (
        <button className="lq-note-toggle" type="button">
          {expanded ? "zwiń" : "więcej"}
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════ */

const LeadQue = (props) => {
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showImportLead, setShowImportLead] = useState(false);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  /* ── fetchers ── */

  const fetchLeadsQue = async () => {
    try {
      const res = await fetch(`${API}/getLeadsQueData`, {
        method: "POST",
        headers,
        body: "{}",
      });
      const data = await res.json();
      setLeads(data);
      await fetchUsers();
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/getUsersList`, {
        method: "POST",
        headers,
        body: "{}",
      });
      const data = await res.json();
      setUsers(data);
      await fetchCompanies();
    } catch {}
  };

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API}/getCompanies`, {
        method: "POST",
        headers,
        body: "{}",
      });
      const data = await res.json();
      setCompanies(data);
    } catch {}
    setLoading(false);
  };

  const fetchDeleteLead = async (id) => {
    try {
      await fetch(`${API}/deleteLead`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id }),
      });
      await MySwal.fire({ icon: "success", title: "Usunięto", text: "lead" });
      getData();
    } catch {}
  };

  const fetchSplitLeads = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/split`, { method: "POST", headers, body: "{}" });
      setTimeout(async () => {
        await MySwal.fire({
          icon: "success",
          title: "Rozdzielono",
          text: "leady",
        });
        getData();
      }, 5000);
    } catch {}
  };

  const fetchEditLead = async (id, field, value) => {
    try {
      await fetch(`${API}/editLead`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          id,
          field,
          value,
          userId: props.user.id,
          userName: props.user.name,
        }),
      });
      await MySwal.fire({ icon: "success", title: "Edytowano", text: "lead" });
      getData();
    } catch {}
  };

  const fetchDeleteAllLeadsQue = async () => {
    try {
      await fetch(`${API}/deleteAllLeadsQue`, {
        method: "POST",
        headers,
        body: "{}",
      });
      await MySwal.fire({
        icon: "success",
        title: "Wyczyszczono",
        text: "kolejkę",
      });
      getData();
    } catch {}
  };

  const getData = () => {
    setLoading(true);
    fetchLeadsQue();
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    return attachCopyLogger(props.user);
  }, []);

  /* ── actions ── */

  const deleteLead = (id) => fetchDeleteLead(id);

  const editLead = (id, field) => {
    MySwal.fire({
      title: "Zmiana",
      input: "text",
      inputLabel: "nowa wartość",
      cancelButtonText: "Anuluj",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return "Wpisz wartość";
        fetchEditLead(id, field, value);
      },
    });
  };

  const editLeadAss = (id) => {
    const model = {};
    users
      .filter((el) => el.companies.trim() === props.user.companies.trim())
      .forEach((u) => {
        model[u.id] = u.name;
      });

    MySwal.fire({
      title: "Wybierz wartość",
      input: "select",
      inputOptions: model,
      inputPlaceholder: "wybierz wartość",
      showCancelButton: true,
      cancelButtonText: "Anuluj",
      inputValidator: (value) =>
        new Promise((resolve) =>
          resolve(value === "" ? "musisz wybrać wartość" : undefined)
        ),
    }).then((result) => {
      if (result.isConfirmed) fetchEditLead(id, "seller", result.value);
    });
  };

  /* ── sorted data ── */
  const sorted = [...leads].sort((a, b) => new Date(b.date) - new Date(a.date));

  /* ── render ── */

  if (loading) return <Loading />;

  return (
    <div className="lq-wrapper">
      {/* Toolbar */}
      <div className="lq-toolbar">
        <div className="lq-toolbar-left">
          <button className="lq-btn lq-btn--success" onClick={getData}>
            Odśwież
          </button>
          <button
            className="lq-btn lq-btn--primary"
            onClick={() => setShowImportLead(true)}
          >
            Import leadów
          </button>
          <button className="lq-btn lq-btn--dark" onClick={fetchSplitLeads}>
            Rozdziel leady
          </button>
          <button
            className="lq-btn lq-btn--muted"
            onClick={() => setShowAddLead(true)}
          >
            Dodaj lead
          </button>
          <button
            className="lq-btn lq-btn--danger"
            onClick={fetchDeleteAllLeadsQue}
          >
            Usuń wszystkie
          </button>
        </div>
        <span className="lq-count">{leads.length} kontaktów</span>
      </div>

      {/* Table */}
      <div className="lq-table-wrap">
        <table className="lq-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nazwa</th>
              <th>Telefon</th>
              <th>E-mail</th>
              <th>Źródło</th>
              <th>ID źródła</th>
              <th>Data</th>
              <th>Uwagi</th>
              <th>Dział</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((el) => (
              <tr key={el.id}>
                <td className="lq-td-id">{el.id}</td>
                <td data-log-field="name" data-log-lead-id={el.id}>
                  {el.name}
                </td>
                <td
                  className="lq-td-mono"
                  data-log-field="phone"
                  data-log-lead-id={el.id}
                >
                  {el.phone}
                </td>
                <td
                  className="lq-td-mono"
                  data-log-field="mail"
                  data-log-lead-id={el.id}
                >
                  {el.mail}
                </td>
                <td data-log-field="source" data-log-lead-id={el.id}>
                  {el.source}
                </td>
                <td>{el.idSource}</td>
                <td className="lq-td-mono">{formatDate(el.date)}</td>
                <td className="lq-td-note">
                  <NoteCell text={el.sourceNote} />
                </td>
                <td>{el.company}</td>
                <td className="lq-td-actions">
                  <div className="lq-actions">
                    <button
                      className="lq-action"
                      onClick={() => deleteLead(el.id)}
                      title="Usuń"
                    >
                      Usuń
                    </button>
                    <button
                      className="lq-action"
                      onClick={() => editLead(el.id, "phone")}
                      title="Edytuj telefon"
                    >
                      Tel.
                    </button>
                    <button
                      className="lq-action"
                      onClick={() => editLead(el.id, "mail")}
                      title="Edytuj e-mail"
                    >
                      Mail
                    </button>
                    <button
                      className="lq-action lq-action--brand"
                      onClick={() => editLeadAss(el.id)}
                      title="Przypisz"
                    >
                      Przypisz
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="lq-empty-row">
                  Brak leadów w kolejce
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showAddLead && (
        <AddLead
          user={props.user}
          close={() => {
            setShowAddLead(false);
            getData();
          }}
          companies={companies}
        />
      )}
      {showImportLead && (
        <ImportLead
          close={() => {
            setShowImportLead(false);
            getData();
          }}
          companies={companies}
        />
      )}
    </div>
  );
};

export default LeadQue;
